import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {testIdProps} from '@github-ui/test-id-props'
import {useIsSafari} from '@github-ui/use-sticky-header/useIsSafari'
import type {PreparedBlock} from '@github-ui/taillights-worker/document'
import {clsx} from 'clsx'
import {Suspense, use, useDeferredValue, useEffect, type CSSProperties} from 'react'
import {TaillightFrame} from './taillight-frame'
import type {TaillightProps} from './taillight-types'
import type {TaillightsController, TaillightsImage} from './taillights-controller'
import {TaillightsGlyphs} from './taillights-glyphs'
import {useTaillightsController, useTaillightsRendering} from './taillights-provider'
import {TAILLIGHTS_LINE_HEIGHT, TAILLIGHTS_PADDING_INLINE} from './taillights-rendering-styles'
import styles from './taillight.module.css'

type PaintProps = {
  block: PreparedBlock
  hidePaint?: boolean
  pseudoText: boolean
}

/** Renders raw source during loading or failure without creating styled glyph spans. */
function PlainBlock({
  block,
  hidePaint,
  pseudoText,
  className,
  style,
}: PaintProps & {className?: string; style?: CSSProperties}) {
  return (
    <pre
      {...testIdProps('taillights-chunk')}
      translate="no"
      className={clsx(styles.Paint, className, hidePaint && styles.hiddenPaint)}
      style={style}
      inert
      aria-hidden
      data-taillights-pseudo-text={pseudoText || undefined}
      data-taillights-text={pseudoText ? block.text : undefined}
    >
      {!pseudoText && block.text}
    </pre>
  )
}

/** Colors browser-rendered glyphs with the decoded worker bitmap, which contains colors rather than text. */
function BitmapPaint({
  image,
  block,
  hidePaint,
  pseudoText,
}: PaintProps & {
  image: TaillightsImage | null
}) {
  return (
    <pre
      {...testIdProps('taillights-chunk')}
      translate="no"
      className={clsx(styles.Paint, image && styles.Bitmap, hidePaint && styles.hiddenPaint)}
      inert
      aria-hidden
      data-mask-status={image ? 'ready' : undefined}
      style={
        image
          ? {
              backgroundImage: `url("${image.url}"), linear-gradient(var(--taillights-default-color) 0 0)`,
              backgroundSize: `${image.width}px ${image.height}px, auto`,
            }
          : undefined
      }
    >
      <TaillightsGlyphs text={block.text} runs={block.glyphRuns} pseudoText={pseudoText} />
      {!pseudoText && block.text.endsWith('\n') && <br inert aria-hidden />}
    </pre>
  )
}

/** Keeps inert glyph DOM mounted while decoded bitmap coloring catches up. */
function DeferredBitmapFrame({
  controller,
  pseudoText,
  ...props
}: Omit<TaillightProps, 'children'> & {controller: TaillightsController; pseudoText: boolean}) {
  const current = controller.image(props.block)
  const decoded = controller.decodedImage(props.block)
  const deferred = useDeferredValue<Promise<TaillightsImage> | null>(current, decoded ? current : null)
  // A deferred image must never color a different block or controller's glyphs.
  const image = decoded ?? (deferred === current ? use(current) : null)
  return (
    <TaillightFrame {...props} renderer="bitmap">
      <BitmapPaint image={image} block={props.block} hidePaint={props.hidePaint} pseudoText={pseudoText} />
    </TaillightFrame>
  )
}

/** Suspends only the paint so the surrounding native source and controls stay mounted. */
function ResolvedBitmapPaint({controller, ...props}: PaintProps & {controller: TaillightsController}) {
  const image = use(controller.image(props.block))
  return <BitmapPaint {...props} image={image} />
}

/** Keeps native source and child controls outside the boundaries that replace loading or failed paint. */
export function TaillightBitmapPaint(props: TaillightProps) {
  const controller = useRetainedController(props.block)
  const pseudoText = useIsSafari()
  const fallback = <PlainBlock block={props.block} hidePaint={props.hidePaint} pseudoText={pseudoText} />
  return controller ? (
    <ErrorBoundary
      key={`${controller.instanceId}:${props.block.imageKey}`}
      boundaryName="TaillightBitmap"
      fallback={fallback}
      onError={props.onRenderError}
    >
      <Suspense fallback={fallback}>
        <ResolvedBitmapPaint
          controller={controller}
          block={props.block}
          hidePaint={props.hidePaint}
          pseudoText={pseudoText}
        />
      </Suspense>
    </ErrorBoundary>
  ) : (
    fallback
  )
}

function useRetainedController(block: PreparedBlock) {
  const controller = useTaillightsController()
  useEffect(() => {
    if (!controller) return
    controller.retain(block)
    return () => controller.release(block)
  }, [controller, block])
  return controller
}

/** Coordinates inert bitmap ownership and deferred frame presentation. */
export function TaillightBitmap(props: TaillightProps) {
  const {block, hidePaint, onRenderError} = props
  const controller = useRetainedController(block)
  const options = useTaillightsRendering()
  const pseudoText = useIsSafari()

  const rendering = controller?.profile ?? options
  const fallback = (
    <PlainBlock
      block={block}
      hidePaint={hidePaint}
      pseudoText={pseudoText}
      className={clsx(styles.Block, props.className)}
      style={{
        ...props.style,
        font:
          rendering?.font ?? 'var(--taillight-font, normal 400 var(--text-body-size-small) var(--fontStack-monospace))',
        lineHeight: `${rendering?.lineHeight ?? TAILLIGHTS_LINE_HEIGHT}px`,
        fontKerning: 'none',
        fontVariantLigatures: 'none',
        ...(rendering?.font === undefined ? {} : {'--taillight-font': rendering.font}),
        '--taillight-line-height': `${rendering?.lineHeight ?? TAILLIGHTS_LINE_HEIGHT}px`,
        '--taillight-padding': `${rendering?.paddingInline ?? TAILLIGHTS_PADDING_INLINE}px`,
        '--taillight-tab-size': rendering?.tabSize ?? 4,
        '--taillight-height': `${block.height}px`,
        '--fgColor-default': block.palette.defaultColor,
        minWidth: block.width,
      }}
    />
  )
  return controller ? (
    <ErrorBoundary
      key={`${controller.instanceId}:${block.imageKey}`}
      boundaryName="TaillightBitmap"
      fallback={fallback}
      onError={onRenderError}
    >
      <Suspense fallback={fallback}>
        <DeferredBitmapFrame {...props} controller={controller} pseudoText={pseudoText} />
      </Suspense>
    </ErrorBoundary>
  ) : (
    fallback
  )
}
